import { useState, createContext, useContext, ReactNode } from 'react';
import firebase from 'firebase';
import { firestore, storage } from 'firebaseSetup';
import { useBrowserStorage } from 'hooks/useBrowserStorage';

const UserContext = createContext<Partial<UserProviderProps>>({});

export const useUserContext = (): Partial<UserProviderProps> =>
  useContext(UserContext);

export const UserProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [localReferences, setLocalReferences] = useBrowserStorage(
    'references',
    undefined,
  );
  const [localAgencies, setLocalAgencies] = useBrowserStorage(
    'agencies',
    [] as Agency[],
  );
  const [localAgencyAssignments, setLocalAgencyAssignments] = useBrowserStorage(
    'agencyAssignments',
    [] as AgencyAssignment[],
  );
  const [localTodos, setLocalTodos] = useBrowserStorage('todos', [] as Todo[]);
  const [references, setReferencs] = useState(localReferences);
  const [files, setFiles] = useState([] as UserFileDetails[]);
  const [agencies, setAgencies] = useState(localAgencies);
  const [agencyAssignments, setAgencyAssignments] = useState(
    localAgencyAssignments,
  );
  const [todos, setTodos] = useState(localTodos);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const persistReferences = (data: Reference[]) => {
    setLocalReferences(data);
    setReferencs(data);
  };

  const persistAgencyData = (data: Agency[]) => {
    setLocalAgencies(data);
    setAgencies(data);
  };

  const persistAgencyAssignmentData = (data: AgencyAssignment[]) => {
    setLocalAgencyAssignments(data);
    setAgencyAssignments(data);
  };

  const persistTodos = (data: Todo[]) => {
    setLocalTodos(data);
    setTodos(data);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const addUser = async (addUserDetails: AddUserDetails) => {
    try {
      const { uid, ...rest } = addUserDetails;
      await firestore
        .collection('users')
        .doc(uid)
        .set({
          ...rest,
        });
    } catch (error) {
      throw error;
    }
  };

  const uploadImage = async (uploadImageDetails: UploadImageDetails) => {
    const { uid, dataURL } = uploadImageDetails;
    const storageRef = storage.ref();
    const photoRef = storageRef.child(`images/${uid}`);

    await photoRef.putString(dataURL, 'data_url');
    const downloadUrl = await photoRef.getDownloadURL();
    return downloadUrl;
  };

  const updateUserDetails = async (updateUserDetails: UpdateUserDetails) => {
    const { userId, ...rest } = updateUserDetails;
    await firestore
      .collection('users')
      .doc(userId)
      .set({ ...rest }, { merge: true });
  };

  const requestReference = async (reference: ReferenceDetails) => {
    try {
      await firestore.collection('references').add({
        created: firebase.firestore.FieldValue.serverTimestamp(),
        status: 'created',
        ...reference,
      });
    } catch (error) {
      throw error;
    }
  };

  const getReferences = async (userId: string) => {
    const collectionRef = firestore.collection('references');
    try {
      await collectionRef
        .where('userId', '==', userId)
        .get()
        .then((querySnapshot) => {
          const data = [] as Reference[];
          querySnapshot.forEach((doc) => {
            const id = doc.id;
            const {
              status,
              email,
              firstname,
              lastname,
              clientFirstname,
              clientLastname,
              created,
            } = doc.data();
            data.push({
              id,
              status,
              email,
              firstname,
              lastname,
              clientFirstname,
              clientLastname,
              created,
            });
          });
          persistReferences(data);
        });
    } catch (e) {
      throw e;
    }
  };

  const getReferenceRequest = async (referenceId: string) => {
    const docRef = firestore.collection('referenceRequests').doc(referenceId);
    try {
      const doc = await docRef.get();
      const data = doc.data();
      if (data) {
        return data;
      }
      return null;
    } catch (e) {
      throw e;
    }
  };

  const deleteReferences = async (ids: string[]) => {
    const collectionRef = firestore.collection('references');
    try {
      for (const id of ids) {
        await collectionRef.doc(id).delete();
      }
    } catch (e) {
      throw e;
    }
  };

  const adminGetReference = async (id: string) => {
    const docRef = firestore.collection('references').doc(id);
    try {
      const doc = await docRef.get();
      const data = doc.data();
      if (data) {
        return data;
      }
      return null;
    } catch (e) {
      throw e;
    }
  };

  const adminGetUserData = async (id: string) => {
    const docRef = firestore.collection('users').doc(id);
    const doc = await docRef.get();
    const data = doc.data();
    return data;
  };

  const getJobs = async (userId: string) => {
    const collectionRef = firestore.collection('userJobs');
    try {
      await collectionRef
        .where('userId', '==', userId)
        .where('seen', '==', false)
        .get()
        .then((querySnapshot) => {
          const data = [] as any[];
          querySnapshot.forEach((doc) => {
            const id = doc.id;
            const { status, email, firstname, lastname, created } = doc.data();
            data.push({
              id,
              status,
              email,
              firstname,
              lastname,
              created,
            });
          });
          return data;
        });
    } catch (e) {
      throw e;
    }
  };

  const submitJobFeedback = async (userId: string, reason: string) => {
    try {
      await firestore.collection('jobFeedback').add({
        userId,
        reason,
        submissionTime: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  };

  const getPrestoredUser = async (refId: string) => {
    const docRef = firestore.collection('referredSignups').doc(refId);
    try {
      const doc = await docRef.get();
      const data = doc.data();
      if (data) {
        return data;
      }
    } catch (e) {
      throw e;
    }
  };

  const saveUserFile = async (userFileDetails: UserFileDetails[]) => {
    const batch = firestore.batch();
    userFileDetails.forEach((doc) => {
      const docRef = firestore.collection('files').doc();
      batch.set(docRef, doc);
    });
    await batch.commit();
  };

  const deleteUserFile = async (userFileDetails: UserFileDetails) => {
    const docRef = firestore.collection('files').doc(userFileDetails.id);
    await docRef.delete();
  };

  const getUserFiles = async (userId: string) => {
    const collectionRef = firestore.collection('files');
    try {
      await collectionRef
        .where('userId', '==', userId)
        .get()
        .then((querySnapshot) => {
          const data = [] as UserFileDetails[];
          querySnapshot.forEach((doc) => {
            const id = doc.id;
            const { userId, filename, url, type, handle, category, tag } =
              doc.data();
            data.push({
              id,
              userId,
              filename,
              url,
              type,
              handle,
              category,
              tag,
            });
          });
          setFiles(data);
        });
    } catch (e) {
      throw e;
    }
  };

  const getAgencies = async () => {
    const collectionRef = firestore.collection('agencies');
    try {
      await collectionRef.get().then((querySnapshot) => {
        const data = [] as Agency[];
        querySnapshot.forEach((doc) => {
          const id = doc.id;
          const agencyData = doc.data() as Omit<Agency, 'id'>;
          data.push({ id, ...agencyData });
        });
        persistAgencyData(data);
      });
    } catch (e) {
      throw e;
    }
  };

  const getAgency = async (agencyId: string) => {
    const docRef = firestore.collection('agencies').doc(agencyId);
    try {
      const doc = await docRef.get();
      const data = doc.data();
      if (data) {
        return { id: doc.id, ...data };
      }
    } catch (e) {
      throw e;
    }
  };

  const updateAgency = async (data: Agency) => {
    const { id, ...rest } = data;
    await firestore
      .collection('agencies')
      .doc(id)
      .set(
        {
          updated: firebase.firestore.FieldValue.serverTimestamp(),
          ...rest,
        },
        { merge: true },
      );
  };

  const sendDocumentsCheck = async (userId: string) => {
    try {
      await firestore.collection('sendDocumentsCheck').add({
        userId,
        created: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  };

  const sendTextNotification = async (userId: string) => {
    try {
      await firestore.collection('textNotifications').add({
        userId,
      });
    } catch (error) {
      throw error;
    }
  };

  const assignUserAgency = async (data: AssignAgencyDetails) => {
    try {
      await firestore.collection('agencyAssignments').add({
        ...data,
        created: firebase.firestore.FieldValue.serverTimestamp(),
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  };

  const getAgencyAssignments = async () => {
    const collectionRef = firestore.collection('agencyAssignments');
    try {
      await collectionRef
        .where('active', '==', true)
        .get()
        .then((querySnapshot) => {
          const data = [] as AgencyAssignment[];
          querySnapshot.forEach((doc) => {
            const id = doc.id;
            const docData = doc.data() as Omit<AgencyAssignment, 'id'>;
            data.push({ id, ...docData });
          });
          persistAgencyAssignmentData(data);
        });
    } catch (e) {
      throw e;
    }
  };

  const updateAgencyAssignment = async (data: UpdateAgencyAssignment) => {
    const { id, ...rest } = data;
    await firestore
      .collection('agencyAssignments')
      .doc(id)
      .set(
        {
          updated: firebase.firestore.FieldValue.serverTimestamp(),
          ...rest,
        },
        { merge: true },
      );
  };

  const getAgencyAssignment = async (userId: string, agencyId: string) => {
    const collectionRef = firestore.collection('agencyAssignments');
    try {
      const assignments = await collectionRef
        .where('user', '==', userId)
        .where('agency', '==', agencyId)
        .get()
        .then((querySnapshot) => {
          const data = [] as AgencyAssignment[];
          querySnapshot.forEach((doc) => {
            const id = doc.id;
            const docData = doc.data() as Omit<AgencyAssignment, 'id'>;
            data.push({ id, ...docData });
          });
          return data;
        });
      const [assignment] = assignments;
      return assignment;
    } catch (e) {
      throw e;
    }
  };

  const getTodos = async (userId: string) => {
    const collectionRef = firestore.collection('todos');
    try {
      await collectionRef
        .where('user', '==', userId)
        .get()
        .then((querySnapshot) => {
          const data = [] as Todo[];
          querySnapshot.forEach((doc) => {
            const id = doc.id;
            const docData = doc.data() as Omit<Todo, 'id'>;
            data.push({ id, ...docData });
          });
          persistTodos(data);
        });
    } catch (e) {
      throw e;
    }
  };

  const submitAgencyReview = async (data: AgencyReviewDetails) => {
    try {
      await firestore.collection('agencyReviews').add({
        ...data,
        created: firebase.firestore.FieldValue.serverTimestamp(),
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } catch (error) {
      throw error;
    }
  };

  const updateTodo = async (data: TodoUpdateDetails) => {
    const { id, ...rest } = data;
    await firestore
      .collection('todos')
      .doc(id)
      .set(
        {
          updated: firebase.firestore.FieldValue.serverTimestamp(),
          ...rest,
        },
        { merge: true },
      );
  };

  return (
    <UserContext.Provider
      value={{
        addUser,
        uploadImage,
        updateUserDetails,
        requestReference,
        getReferences,
        getReferenceRequest,
        references,
        deleteReferences,
        adminGetReference,
        adminGetUserData,
        getJobs,
        submitJobFeedback,
        getPrestoredUser,
        saveUserFile,
        getUserFiles,
        files,
        getAgencies,
        getAgency,
        updateAgency,
        agencies,
        sendDocumentsCheck,
        sendTextNotification,
        deleteUserFile,
        assignUserAgency,
        agencyAssignments,
        getAgencyAssignments,
        updateAgencyAssignment,
        getAgencyAssignment,
        getTodos,
        todos,
        submitAgencyReview,
        updateTodo,
        drawerOpen,
        toggleDrawer,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
